import type { PublicFacilityResponse } from "@bw/shared/types/generated";
import type { ReactNode } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { FavouriteFacilityButton } from "./FavouriteFacilityButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CallRounded from "@mui/icons-material/CallRounded";
import DirectionsRounded from "@mui/icons-material/DirectionsRounded";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";
import { initials } from "../../../common/avatar";

/**
 * Join an address' parts into a single human line ("123 Court St, Seattle, WA"),
 * skipping any absent piece — never a stray comma for a missing field and never a
 * fabricated value. The postal code is DELIBERATELY omitted: a player landing on
 * a facility page needs the street + city to orient, not the ZIP, and showing it
 * adds visual noise. Returns null when there is nothing to show.
 */
function addressLine(
  address: PublicFacilityResponse["address"],
): string | null {
  if (!address) return null;
  const cityState = [address.city, address.state].filter(Boolean).join(", ");
  const line = [address.line1, cityState || null].filter(Boolean).join(", ");
  return line.trim() ? line : null;
}

function phoneHref(value: string): string | null {
  const normalized = value.replace(/[^+\d]/g, "");
  return normalized ? `tel:${normalized}` : null;
}

/** Directions open the venue's address in the user's map app; omitted without one. */
function directionsHref(facility: PublicFacilityResponse): string | null {
  const line = addressLine(facility.address ?? null) || facility.location;
  return line ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${facility.name}, ${line}`)}` : null;
}

/**
 * Compact identity row (approved facility home): photo or initials tile, the
 * name as the page's h1, "address · N courts", then the primary action with
 * directions and support as icons. Independent of secondary facility reads.
 */
export function FacilityHero({
  facility,
  back,
  primaryAction,
  children,
}: {
  facility: PublicFacilityResponse;
  /** The back control, shown at the start of the identity row. */
  back?: ReactNode;
  /** The service-gated main action (Book a court). */
  primaryAction?: ReactNode;
  children?: ReactNode;
}) {
  const line = addressLine(facility.address ?? null) || facility.location;
  const supportEmail = facility.support_email?.trim() || null;
  const supportPhone = facility.support_phone?.trim() || null;
  const supportPhoneHref = supportPhone ? phoneHref(supportPhone) : null;
  const directions = directionsHref(facility);
  const cover = facility.photos?.[0];
  const meta = [
    line,
    facility.court_count > 0 ? `${facility.court_count} ${facility.court_count === 1 ? "court" : "courts"}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Stack component="section" aria-label="Facility overview" spacing={1.5}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", minWidth: 0 }}>
        {back}
        {cover ? (
          <Box component="img" src={cover.url} alt="" sx={{ width: 48, height: 48, borderRadius: 2.5, objectFit: "cover", flexShrink: 0 }} />
        ) : (
          <Avatar
            variant="rounded"
            aria-hidden
            sx={{ width: 48, height: 48, borderRadius: 2.5, bgcolor: "action.hover", color: "text.secondary", fontWeight: 700 }}
          >
            {initials(facility.name)}
          </Avatar>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography component="h1" sx={{ fontSize: "1.25rem", fontWeight: 700, lineHeight: 1.25, overflowWrap: "anywhere" }}>
            {facility.name}
          </Typography>
          {meta ? (
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
              {meta}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} role="group" aria-label="Facility actions" sx={{ alignItems: "center" }}>
          {primaryAction ? <Box sx={{ flex: 1 }}>{primaryAction}</Box> : <Box sx={{ flex: 1 }} />}
          <FavouriteFacilityButton facilityId={facility.facility_id} facilityName={facility.name} />
          {directions ? (
            <Tooltip title="Directions">
              <IconButton component="a" href={directions} target="_blank" rel="noopener noreferrer" aria-label="Directions (opens in a new tab)" sx={iconAction}>
                <DirectionsRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          {supportEmail ? (
            <Tooltip title="Email support">
              <IconButton component="a" href={`mailto:${supportEmail}`} aria-label="Email support" sx={iconAction}>
                <MailOutlineRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          {supportPhoneHref ? (
            <Tooltip title="Call support">
              <IconButton component="a" href={supportPhoneHref} aria-label="Call support" sx={iconAction}>
                <CallRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
      </Stack>
      {children}
    </Stack>
  );
}

const iconAction = { border: 1, borderColor: "divider", borderRadius: 2.5 } as const;
